import { useEffect, useMemo, useState } from "react";
import BaseButton from "../../components/ui/BaseButton";
import BaseTooltip from "../../components/ui/BaseTooltip";
import { brandPrimaryColor } from "../../utils/variables";
import BaseWrapper from "../../components/ui/BaseWrapper";
import useAuth from "../../hooks/useAuth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { declineOffer, fetchCustomerCounterOffer } from "../../services/api/offers";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../store/toast/toastSlice";
import CounterOfferModal from "./CounterOfferModal";
import RequestOfferModal from "./RequestOfferModal";
import MarkIncompleteModal from "./MarkIncompleteModal";
import {
  markWorkOrderAsPayable,
  markWorkOrderAsPOPending,
  publishDraftedWorkOrder,
  republishWorkOrderApi,
  undoApproval,
  updateWorkOrderApi,
  updateWorkOrderStatus,
} from "../../services/api/workOrder";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateWorkOrder } from "../../hooks/useUpdateWorkOrder";
import CheckInModal from "./CheckInModal";
import CheckOutModal from "./CheckOutModal";
import WOConfirmationModal from "./WOConfirmationModal";
import OnTheWayModal from "./OnTheWayModal";
import RunningLateModal from "./RunningLateModal";
import CancelAssignmentModal from "./CancelAssignmentModal";
import FeedbackModal from "./FeedbackModal";
import FeedbackSuccessModal from "./FeedbackSuccessModal";
import WithdrawCounterOfferModal from "./WithdrawCounterOfferModal";
import UnpublishModal from "./UnpublishModal";
import CancelByClientModal from "./CancelByClientModal";
import CompleteProfileModal from "./CompleteProfileModal";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import MarkCompleteModal from "./MarkCompleteModal";
import HalfDayCounterOfferModal from "./HalfDayCounterOfferModal";
import FullDayCounterOfferModal from "./FullDayCounterOfferModal";
import FixedCounterOfferModal from "./FixedCounterOfferModal";
import CustomerCounterOfferModal from "./CustomerCounterOfferModal";
import WithdrawCustomerCounterOfferModal from "./WithdrawCustomerCounterOfferModal";
import AssignToMemberModal from "./AssignToMemberModal";
import { ModuleType, UserType, WorkOrderStatusType } from "../../utils/enum";
import ApproveWorkOrderModal from "./ApproveWorkOrderModal";
import { approveWorkOrder } from "../../services/api/workOrder";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import AddWorkOrderNoteModal from "./AddWorkOrderNoteModal";
import AddEditTaskModal from "../TaskBoard/components/AddEditTaskModal";
import { setFeedbackModalOpen } from "../../store/modalsManagement/modalsManagementSlice";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { isStatusBetween } from "../../utils/helpers";
import UserProfileLink from "../../components/ui/UserProfileLink";

dayjs.extend(utc);
dayjs.extend(timezone);

const ActionBar = ({ userAllowedAction, refetchUserAllowedAction }) => {
  const { userData } = useCurrentUser();
  const navigate = useNavigate();
  const { authRole, auth, getPermissionForModule } = useAuth();
  const { hasWriteAccess: hasVerifyWorkOrderAccess } = getPermissionForModule(
    ModuleType.ACTION_VERIFY_WORK_ORDER,
  );
  const { hasWriteAccess: hasReviewWorkOrderAccess } = getPermissionForModule(
    ModuleType.ACTION_REVIEW_WORK_ORDER,
  );
  const { hasWriteAccess: hasApproveWorkOrderAccess } = getPermissionForModule(
    ModuleType.ACTION_APPROVE_WORK_ORDER,
  );

  const { hasWriteAccess: hasCancelWorkOrderAccess } = getPermissionForModule(
    ModuleType.ACTION_CANCEL_WORK_ORDER,
  );
  const { updateWorkOrder } = useUpdateWorkOrder();
  const params = useParams();
  const [counterOfferModalOpen, setCounterOfferModalOpen] = useState(false);
  const [requestOfferModalOpen, setRequestOfferModalOpen] = useState(false);
  const [counterOfferMode, setCounterOfferMode] = useState("");
  const [withdrawCounterOfferModalOpen, setWithdrawCounterOfferModalOpen] = useState(false);

  const [woConfirmationModalOpen, setWOConfirmationModalOpen] = useState(false);
  const [woConfirmationModalData, setWOConfirmationModalData] = useState({
    title: "",
    actionType: "",
  });
  const [assignToMemberModalOpen, setAssignToMemberModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [feedbackSuccessModalOpen, setFeedbackSuccessModalOpen] = useState(false);
  const [isEditFeedbackMode, setIsEditFeedbackMode] = useState(false);
  const [markCompleteModalOpen, setMarkCompleteModalOpen] = useState(false);
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [onTheWayModalOpen, setOnTheWayModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [isMarkDisputeConfirmationModalOpen, setIsMarkDisputeConfirmationModalOpen] =
    useState(false);
  const [isMarkPayableConfirmationModalOpen, setIsMarkPayableConfirmationModalOpen] =
    useState(false);
  const [runningLateModalOpen, setRunningLateModalOpen] = useState(false);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const [markIncompleteModalOpen, setMarkIncompleterModalOpen] = useState(false);
  const [checkOutModalOpen, setCheckOutModalOpen] = useState(false);
  const [cancelAssignmentModalOpen, setCancelAssignmentModalOpen] = useState(false);

  const [cancelByClientOpen, setCancelByClientOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isCustomerApproveConfirmOpen, setIsCustomerApproveConfirmOpen] = useState(false);
  const [customerCounterOfferModalOpen, setCustomerCounterOfferModalOpen] = useState(false);
  const [withdrawCustomerCounterOfferModalOpen, setWithdrawCustomerCounterOfferModalOpen] =
    useState(false);
  // The pending counter offer being edited (Edit Counter Offer button), which
  // reopens the counter offer form on it.
  const [editingCustomerCounterOffer, setEditingCustomerCounterOffer] = useState(null);

  const dispatch = useDispatch();
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);

  // While a counter offer the client sent awaits the customer's answer it can be
  // viewed, edited or withdrawn, not re-sent — so the client's button depends on
  // whether one is pending. Only a customer's ticket reaches PENDING_APPROVAL.
  const isClientReviewingTicket =
    authRole === UserType.CLIENT &&
    workOrderDetail?.status === WorkOrderStatusType.PENDING_APPROVAL;
  const { data: customerCounterOfferData, isSuccess: isCustomerCounterOfferLoaded } = useQuery({
    queryKey: ["customerCounterOffer", workOrderDetail?.id],
    queryFn: () => fetchCustomerCounterOffer(workOrderDetail.id),
    enabled: !!workOrderDetail?.id && isClientReviewingTicket,
  });
  const pendingCustomerCounterOffer =
    customerCounterOfferData?.data?.status === "PENDING" ? customerCounterOfferData.data : null;
  // Once the customer has accepted a counter offer (stored as ASSIGNED) the rates
  // are settled, so no further counter offer is offered.
  const isCustomerCounterOfferAccepted = customerCounterOfferData?.data?.status === "ASSIGNED";
  // Neither button until the server has told us whether one was already sent — not
  // while loading, and not if that request failed — so Counter Offer never shows up
  // on a ticket that already has a pending one.
  const canShowCounterOfferButtons = isClientReviewingTicket && isCustomerCounterOfferLoaded;

  // Customer "Approve" is shown once the engineer marks the work done, but stays
  // disabled until the client has verified it. The tooltip is intentionally
  // generic: we don't expose the internal review/verify step to customers.
  const customerApproveDisabledReason =
    authRole === UserType.CUSTOMER && workOrderDetail?.status === "WORKDONE"
      ? "No action is required from you at this stage. We'll notify you once the work order is ready for your approval."
      : "";
  const isCustomerApproveDisabled = Boolean(customerApproveDisabledReason);

  const hasCurrentActorGivenFeedback =
    authRole === UserType.CLIENT
      ? !!userAllowedAction?.isFeedbackGivenByClient
      : !!userAllowedAction?.isFeedbackGivenByEngineer;

  const FEEDBACK_STATUSES = ["WORKDONE", "APPROVED", "VERIFIED", "INVOICED", "DISPUTED"];

  const isWithinEngineerFeedbackWindow = !!userAllowedAction?.canEngineerGiveFeedback;

  const isWithinClientFeedbackWindow = [...FEEDBACK_STATUSES, "PAID"].includes(
    workOrderDetail?.status,
  );

  const isDetailForCurrentWorkOrder =
    !!workOrderDetail?.id && String(workOrderDetail.id) === String(params.id);

  const feedbackModalOpen = useSelector((state) => {
    const explicit = state.modalsManagement.value.feedbackModalOpenByWorkOrder[params.id];
    if (explicit !== undefined) return !!explicit;
    return (
      !!userAllowedAction &&
      workOrderDetail?.status === WorkOrderStatusType?.WORKDONE &&
      isDetailForCurrentWorkOrder &&
      !hasCurrentActorGivenFeedback &&
      authRole !== UserType.FREELANCER
    );
  });

  const feedbackModalExplicit = useSelector(
    (state) => state.modalsManagement.value.feedbackModalOpenByWorkOrder[params.id],
  );
  const { closeOutNotesModalOpen } = useSelector((state) => state.modalsManagement.value);

  const isAssignedEngineerOnWorkOrder =
    !!workOrderDetail?.engineer?.id && workOrderDetail?.engineer?.id === auth?.user?.id;

  const isWithinAllowedWindow = useMemo(() => {
    if (!workOrderDetail?.orderStartDate || !workOrderDetail?.orderStartTime) return false;
    const workOrderTZ = workOrderDetail?.timeZone || "UTC";

    const startTimeString = `${dayjs(workOrderDetail.orderStartDate).format(
      "YYYY-MM-DD",
    )}T${dayjs(workOrderDetail.orderStartTime).format("HH:mm:ss")}`;

    const startTime = dayjs.tz(startTimeString, workOrderTZ);

    let hoursBefore = 2;

    if (workOrderDetail?.subStatus === "READY_TO_GO") {
      hoursBefore = 4;
    } else if (workOrderDetail?.subStatus === "CONFIRMED") {
      hoursBefore = 36;
    }

    const allowedTimeBefore = startTime.subtract(hoursBefore, "hour");
    const now = dayjs().tz(workOrderTZ);

    return now.isAfter(allowedTimeBefore);
  }, [
    workOrderDetail?.orderStartDate,
    workOrderDetail?.orderStartTime,
    workOrderDetail?.subStatus,
    workOrderDetail?.timeZone,
  ]);

  const {
    isPending: isUpdatingLoading,
    error: isUpdatingError,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: (data) => {},
  });

  const {
    isPending: isUpdatingStatusLoading,
    error: isUpdatingStatusError,
    mutateAsync: updateWorkOrderStatusMutation,
  } = useMutation({
    mutationFn: updateWorkOrderStatus,
  });

  const {
    isPending: isPublishDraftedWorkOrderApiLoading,
    error: isPublishDraftedWorkOrderApiError,
    mutateAsync: publishDraftedWorkOrderMutation,
  } = useMutation({
    mutationFn: publishDraftedWorkOrder,
  });

  const {
    isLoading: isWorkOrderRepublishLoading,
    error: isWorkOrderRepublishError,
    data: republishWorkOrderData,
    refetch: refetchRepublishWorkOrder,
  } = useQuery({
    queryKey: ["republishWorkOrder"],
    queryFn: () => republishWorkOrderApi(params.id),
    enabled: false,
  });

  const { isPending: isUndoApprovalApiLoading, mutateAsync: undoApprovalMutation } = useMutation({
    mutationFn: undoApproval,
  });

  const {
    isPending: isMarkWorkOrderAsPOPendingApiLoading,
    mutateAsync: markWorkOrderAsPOPendingMutation,
  } = useMutation({
    mutationFn: markWorkOrderAsPOPending,
  });

  const {
    isPending: isMarkWorkOrderAsPayableApiLoading,
    mutateAsync: markWorkOrderAsPayableMutation,
  } = useMutation({
    mutationFn: markWorkOrderAsPayable,
  });

  const {
    // status,
    error: isDelineError,
    mutateAsync: declineOfferMutation,
  } = useMutation({
    mutationFn: declineOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Job Updated Successfully",
          type: "success",
        }),
      );
    },
  });

  const { isPending: isApproveWorkOrderLoading, mutateAsync: approveWorkOrderMutation } =
    useMutation({
      mutationFn: approveWorkOrder,
      onSuccess: () => {
        updateWorkOrder();
        setIsCustomerApproveConfirmOpen(false);
        dispatch(
          showToast({
            message: "Work order approved successfully",
            type: "success",
          }),
        );
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || "Failed to approve work order";
        dispatch(
          showToast({
            message: errorMessage,
            type: "error",
          }),
        );
      },
    });

  /* useEffect(() => {
    if (
      userAllowedAction &&
      !userAllowedAction?.isFeedbackGivenByClient &&
      workOrderDetail?.status === WorkOrderStatusType?.APPROVED &&
      authRole === UserType.CLIENT
    ) {
      setFeedbackModalOpen(true);
    } else {
      setFeedbackModalOpen(false);
    }
  }, [userAllowedAction, workOrderDetail]); */

  useEffect(() => {
    if (feedbackModalExplicit !== undefined) return;
    if (!isDetailForCurrentWorkOrder) return;

    if (
      userAllowedAction &&
      !userAllowedAction?.isFeedbackGivenByEngineer &&
      workOrderDetail?.status === WorkOrderStatusType?.APPROVED &&
      authRole === UserType.FREELANCER &&
      !userData?.isCompanyMember &&
      !userData?.isCompanyOwner
    ) {
      dispatch(
        setFeedbackModalOpen({
          workOrderId: params.id,
          isOpen: true,
        }),
      );
    }
  }, [
    userAllowedAction,
    workOrderDetail,
    userData,
    feedbackModalExplicit,
    isDetailForCurrentWorkOrder,
  ]);

  async function handleNotInterestedButton(userId, workOrderId) {
    try {
      let payload = {
        workOrderId,
        status: "DECLINED",
        ...(userAllowedAction?.routedOfferId && { offerId: userAllowedAction.routedOfferId }),
      };
      await declineOfferMutation(payload);
      await updateWorkOrder();
      await refetchUserAllowedAction();
    } catch (error) {
      dispatch(
        showToast({
          message: isDelineError?.response?.data?.message,
          type: "error",
        }),
      );
    }
  }

  const handlePublishButton = async () => {
    const payload = {
      id: workOrderDetail.id,
      status: "PUBLISHED",
    };
    try {
      if (workOrderDetail.subStatus === "CANCELLED") {
        await refetchRepublishWorkOrder();
      } else {
        await await publishDraftedWorkOrderMutation(workOrderDetail.id);
      }

      updateWorkOrder();
      dispatch(
        showToast({
          message:
            authRole === UserType.CUSTOMER && workOrderDetail?.status === "DRAFT"
              ? "Work order has been submitted for acceptance."
              : "Work Order Published",
          type: "success",
        }),
      );
    } catch (error) {
      // A draft can be saved half-finished, so publishing is where the missing
      // pieces surface. The API returns them as `missingFields`; list them one
      // per line and keep the toast open longer, since there can be several and
      // the user has to carry them over to the edit form.
      const missingFields = error.response?.data?.missingFields;

      dispatch(
        showToast({
          message: missingFields?.length
            ? `Cannot publish. Please complete:\n${missingFields
                .map((field) => `• ${field}`)
                .join("\n")}`
            : error.response?.data?.message || "Could not publish this work order.",
          type: "error",
          ...(missingFields?.length ? { autoClose: 8000, style: { whiteSpace: "pre-line" } } : {}),
        }),
      );
    }
  };

  // Open the customer-raised ticket in the edit wizard so the client completes the
  // marketplace details and publishes it there with the full create validations.
  const handleReviewPublishButton = () => {
    navigate(`/workOrder/edit/summary/${workOrderDetail.id}`);
  };

  const handleReadyToGo = async () => {
    setWOConfirmationModalOpen(true);
    setWOConfirmationModalData({
      title: "Ready to go",
      actionType: "Ready to go",
    });
  };

  const handleCheckIn = async (id) => {
    setCheckInModalOpen(true);
  };

  const handleOnTheWay = () => {
    setOnTheWayModalOpen(true);
  };

  const handleCheckOut = () => {
    setCheckOutModalOpen(true);
  };
  const handleMarkCompleteButton = () => {
    // Mirrors the API rule (markWorkOrderComplete): the client's custom tasks are
    // the checklist the engineer works to, so only they are held to it — the client
    // owns the checklist and may close the job over it. Checked here as well as
    // server-side so the engineer is stopped before filling in the sign-off sheet.
    const outstandingCustomTasks =
      authRole === UserType.FREELANCER
        ? (workOrderDetail?.tasks || []).filter(
            (task) => task.taskType === "COMPLETE_CUSTOM_TASK" && !task.completedTask?.isCompleted,
          )
        : [];

    if (outstandingCustomTasks.length > 0) {
      const titles = outstandingCustomTasks.map((task) => task.title || "Untitled task");
      dispatch(
        showToast({
          message: `Complete the remaining task${titles.length > 1 ? "s" : ""} first: ${titles.join(", ")}`,
          type: "error",
        }),
      );
      return;
    }

    setMarkCompleteModalOpen(true);
  };

  const handleMarkDispute = async (payload) => {
    try {
      await updateWorkOrderStatusMutation(payload);

      updateWorkOrder();
      dispatch(
        showToast({
          message: "Work Order Disputed",
          type: "success",
        }),
      );
      setIsMarkDisputeConfirmationModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleMarkPayable = async (workOrderId) => {
    try {
      await markWorkOrderAsPayableMutation(workOrderId);

      navigate(`/workOrder/details/${workOrderId}?fromCustomerBoard=true`);
      updateWorkOrder();
      dispatch(
        showToast({
          message: "Work Order Payable",
          type: "success",
        }),
      );
      setIsMarkPayableConfirmationModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const handleUndoApproval = async () => {
    try {
      await undoApprovalMutation(params.id);
      updateWorkOrder();
      dispatch(
        showToast({
          message: "Action Completed Successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message,
          type: "error",
        }),
      );
    }
  };
  const handlePOPending = async () => {
    try {
      await markWorkOrderAsPOPendingMutation(params.id);
      updateWorkOrder();
      dispatch(
        showToast({
          message: "Action Completed Successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const canMarkPOPending = isStatusBetween(
    workOrderDetail?.status === WorkOrderStatusType.VERIFIED
      ? workOrderDetail?.status
      : workOrderDetail?.customerStatus,
    WorkOrderStatusType.VERIFIED,
    WorkOrderStatusType.APPROVED,
  );

  return (
    <div className="border-b border-zinc-300 py-2 lg:py-[10px] bg-white overflow-auto">
      <BaseWrapper>
        <div className="flex flex-wrap items-center gap-2 lg:grid lg:grid-cols-[1fr_auto] lg:items-start">
          <div className="gap-2 flex flex-wrap">
            {authRole === "CLIENT" && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary  min-w-[90px] max-w-[90px] h-8"
              >
                Copy
              </BaseButton>
            )}

            {authRole === "CLIENT" && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary  min-w-[150px] max-w-[150px] h-8 "
              >
                Save as Template
              </BaseButton>
            )}
            {authRole === "CLIENT" && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary  min-w-[85px] max-w-[85px] h-8 "
              >
                Tags
              </BaseButton>
            )}

            <BaseButton
              style={{
                border: `1px solid ${brandPrimaryColor}`,
                paddingTop: 0,
                paddingBottom: 0,
              }}
              fullWidth={true}
              className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary  min-w-[85px] max-w-[85px]  h-8 "
            >
              Print
            </BaseButton>

            {(workOrderDetail?.status === "ASSIGNED" ||
              workOrderDetail?.status === "INPROGRESS" ||
              workOrderDetail?.status === "INCOMPLETE") && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary min-w-[140px] max-w-[140px] h-8 "
              >
                Report problem
              </BaseButton>
            )}
            {authRole === "CLIENT" && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary min-w-[115px] max-w-[115px] h-8 "
                onClick={() => {
                  setIsAddNoteModalOpen(true);
                }}
              >
                Add Note
              </BaseButton>
            )}
            {authRole === "CLIENT" && (
              <BaseButton
                style={{
                  border: `1px solid ${brandPrimaryColor}`,
                  paddingTop: 0,
                  paddingBottom: 0,
                }}
                fullWidth={true}
                className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary min-w-[145px] max-w-[145px] h-8"
                onClick={() => setIsCreateTaskModalOpen(true)}
              >
                Create New Task
              </BaseButton>
            )}

            {/* {workOrderDetail?.status === WorkOrderStatusType.APPROVED &&
              authRole === UserType.CLIENT && (
                <BaseButton
                  isLoading={isUndoApprovalApiLoading}
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={true}
                  className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary  min-w-[140px] max-w-[140px] h-8"
                  onClick={handleUndoApproval}
                >
                  Undo Approval
                </BaseButton>
              )} */}
            {canMarkPOPending &&
              workOrderDetail?.subStatus !== WorkOrderStatusType.PO_PENDING &&
              authRole === UserType.CLIENT && (
                <BaseButton
                  isLoading={isMarkWorkOrderAsPOPendingApiLoading}
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={true}
                  className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary  min-w-[150px] max-w-[150px] h-8"
                  onClick={handlePOPending}
                >
                  Mark PO Pending
                </BaseButton>
              )}

            {authRole === "CLIENT" &&
              workOrderDetail?.status === "ASSIGNED" &&
              hasCancelWorkOrderAccess && (
                <BaseButton
                  onClick={() => setCancelByClientOpen(true)}
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={true}
                  className="bg-[#F6F6F6]  hover:bg-primary hover:text-white text-primary min-w-[130px] h-8 max-w-[150px]"
                >
                  Cancel
                </BaseButton>
              )}

            {authRole == "FREELANCER" &&
              (userAllowedAction?.isRouted || userAllowedAction?.isAccepted) &&
              !userAllowedAction?.isWorkOrderAlreadyAssigned && (
                <BaseButton
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={true}
                  className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary   max-w-[140px] min-w-[140px]  h-8"
                  onClick={() => handleNotInterestedButton(auth.user.id, workOrderDetail.id)}
                >
                  Not Interested
                </BaseButton>
              )}

            {authRole === "FREELANCER" &&
              (workOrderDetail?.subStatus === "NOT_CONFIRMED" ||
                workOrderDetail?.subStatus === "CONFIRMED" ||
                workOrderDetail?.subStatus === "READY_TO_GO") &&
              workOrderDetail.status !== "CANCELLED" && (
                <BaseButton
                  onClick={() => setCancelAssignmentModalOpen(true)}
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={true}
                  className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary   min-w-[180px]  max-w-[180px]  h-8"
                >
                  Cancel Assignment
                </BaseButton>
              )}
          </div>

          {authRole === "FREELANCER" && (
            <div className="gap-2 flex flex-wrap">
              {workOrderDetail?.status === "PUBLISHED" && (
                <>
                  {(!workOrderDetail?.isCounterOffersDisable && userAllowedAction?.isRequested) ||
                    (!workOrderDetail?.isCounterOffersDisable &&
                      userAllowedAction?.isWorkOrderCancelByEngineer) ||
                    userAllowedAction?.isDeclined ||
                    userAllowedAction?.isWorkOrderAlreadyAssigned ||
                    (!userAllowedAction?.isCounter && !userAllowedAction?.isAccepted && (
                      <BaseButton
                        fullWidth={true}
                        disabled={userAllowedAction?.isDeclined}
                        onClick={() => {
                          if (userData?.status !== "APPROVED") {
                            setIsProfileCompleted(true);
                            return;
                          }
                          setCounterOfferMode("add");
                          setCounterOfferModalOpen(true);
                        }}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                      >
                        Counter Offer
                      </BaseButton>
                    ))}

                  {userAllowedAction?.isRouted &&
                    (userData?.isCompanyOwner || userData?.isCompanyMember
                      ? !!workOrderDetail?.engineerPaymentType &&
                        !!(
                          workOrderDetail?.engineerHourlyRate ||
                          workOrderDetail?.engineerFixedAmount ||
                          workOrderDetail?.engineerHalfDayRate ||
                          workOrderDetail?.engineerFullDayRate
                        )
                      : true) && (
                      <BaseButton
                        fullWidth={true}
                        onClick={() => {
                          if (userData?.status !== "APPROVED") {
                            setIsProfileCompleted(true);
                            return;
                          }
                          setRequestOfferModalOpen(true);
                        }}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                      >
                        Accept
                      </BaseButton>
                    )}
                  {userAllowedAction?.isDeclined ||
                    userAllowedAction?.isRouted ||
                    userAllowedAction?.isCounter ||
                    userAllowedAction?.isWorkOrderCancelByEngineer ||
                    (!userAllowedAction?.isRequested &&
                      !userAllowedAction?.isAccepted &&
                      (userData?.isCompanyOwner
                        ? !!workOrderDetail?.engineerPaymentType &&
                          !!(
                            workOrderDetail?.engineerHourlyRate ||
                            workOrderDetail?.engineerFixedAmount ||
                            workOrderDetail?.engineerHalfDayRate ||
                            workOrderDetail?.engineerFullDayRate
                          )
                        : true) && (
                        <BaseButton
                          disabled={userAllowedAction?.isDeclined}
                          onClick={() => {
                            if (userData?.status !== "APPROVED") {
                              setIsProfileCompleted(true);
                              return;
                            }

                            setRequestOfferModalOpen(true);
                          }}
                          style={{
                            border: `1px solid ${brandPrimaryColor}`,
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          fullWidth={true}
                          className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                        >
                          Request
                        </BaseButton>
                      ))}

                  {userAllowedAction?.isRequested &&
                    !userAllowedAction?.isWorkOrderAlreadyAssigned && (
                      <BaseButton
                        fullWidth={true}
                        onClick={() => {
                          setWOConfirmationModalOpen(true);
                          setWOConfirmationModalData({
                            title: "Withdraw Request",
                            actionType: "Withdraw",
                          });
                        }}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className="bg-primary text-white  min-w-[160px] max-w-[160px] h-8"
                      >
                        Withdraw Request
                      </BaseButton>
                    )}

                  {userAllowedAction?.isCounter &&
                    !userAllowedAction?.isWorkOrderAlreadyAssigned && (
                      <BaseButton
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={false}
                        className="bg-primary text-white h-8"
                        onClick={() => {
                          setCounterOfferMode("edit");
                          setCounterOfferModalOpen(true);
                        }}
                      >
                        Edit Counter Offer
                      </BaseButton>
                    )}

                  {userAllowedAction?.isCounter &&
                    !userAllowedAction?.isWorkOrderAlreadyAssigned && (
                      <BaseButton
                        onClick={() => {
                          setWithdrawCounterOfferModalOpen(true);
                        }}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={false}
                        className="bg-primary text-white h-8"
                      >
                        Withdraw Counter Offer
                      </BaseButton>
                    )}
                </>
              )}
              {workOrderDetail?.status === "ASSIGNED" && (
                <>
                  {userData?.isCompanyOwner ? (
                    (() => {
                      const isCompanyPrimaryAssignee =
                        workOrderDetail?.assignedFreelanceCompany?.owner?.id === auth?.user?.id;

                      if (!isCompanyPrimaryAssignee) {
                        return null;
                      }

                      const assignedMemberOffer = workOrderDetail?.assignedFreelanceCompanyId
                        ? workOrderDetail?.offers?.find(
                            (o) =>
                              o.status === "ASSIGNED" &&
                              o.user?.id !== auth.user.id &&
                              o.createdById === auth.user.id,
                          )
                        : null;

                      if (assignedMemberOffer) {
                        return (
                          <div className="flex items-center gap-2 px-3 py-1 rounded-lg border border-[#E0E0E0] bg-[#F8F9FA] h-8">
                            <i className="fa-solid fa-user-check text-primary text-xs"></i>
                            <p className="text-sm text-dark whitespace-nowrap">
                              Assigned to:{" "}
                              <UserProfileLink
                                as="strong"
                                userId={assignedMemberOffer.user?.id}
                                className="text-primary"
                                linkClassName="cursor-pointer hover:underline"
                              >
                                {assignedMemberOffer.user?.firstName}{" "}
                                {assignedMemberOffer.user?.lastName}
                              </UserProfileLink>
                            </p>
                          </div>
                        );
                      }

                      if (userAllowedAction?.isWorkOrderConfirmByEngineer) {
                        return (
                          <BaseButton
                            fullWidth={true}
                            onClick={() => setAssignToMemberModalOpen(true)}
                            style={{
                              border: `1px solid ${brandPrimaryColor}`,
                              paddingTop: 0,
                              paddingBottom: 0,
                            }}
                            className="bg-primary text-white min-w-[160px] max-w-[180px] h-8"
                          >
                            Assign to Member
                          </BaseButton>
                        );
                      }

                      if (
                        !userAllowedAction?.isWorkOrderCancelByEngineer &&
                        !userAllowedAction?.isStartTime
                      ) {
                        return (
                          <BaseButton
                            fullWidth={true}
                            onClick={() => {
                              setWOConfirmationModalData({
                                title: "Confirm Work Order",
                                actionType: "Confirm",
                              });
                              setWOConfirmationModalOpen(true);
                            }}
                            style={{
                              border: `1px solid ${brandPrimaryColor}`,
                              paddingTop: 0,
                              paddingBottom: 0,
                            }}
                            className="bg-primary text-white min-w-[120px] max-w-[120px] h-8"
                          >
                            Confirm
                          </BaseButton>
                        );
                      }

                      return null;
                    })()
                  ) : (
                    <>
                      {(!userAllowedAction?.isWorkOrderAlreadyAssigned ||
                        userAllowedAction?.isAssignedMember) &&
                        !userAllowedAction?.isWorkOrderCancelByEngineer &&
                        !userAllowedAction?.isWorkOrderConfirmByEngineer &&
                        !userAllowedAction?.isStartTime && (
                          <BaseButton
                            fullWidth={true}
                            onClick={() => {
                              setWOConfirmationModalData({
                                title: "Confirm Work Order",
                                actionType: "Confirm",
                              });
                              setWOConfirmationModalOpen(true);
                            }}
                            style={{
                              border: `1px solid ${brandPrimaryColor}`,
                              paddingTop: 0,
                              paddingBottom: 0,
                            }}
                            className="bg-primary text-white min-w-[120px] max-w-[120px] h-8"
                          >
                            Confirm
                          </BaseButton>
                        )}
                      {userAllowedAction?.isWorkOrderConfirmByEngineer &&
                        !userAllowedAction?.isStartTime && (
                          <BaseButton
                            fullWidth={true}
                            onClick={handleReadyToGo}
                            style={{
                              border: `1px solid ${brandPrimaryColor}`,
                              paddingTop: 0,
                              paddingBottom: 0,
                            }}
                            className="bg-primary text-white min-w-[120px] max-w-[120px] h-8"
                            disabled={!isWithinAllowedWindow}
                          >
                            Ready to go
                          </BaseButton>
                        )}
                    </>
                  )}
                </>
              )}
              {isAssignedEngineerOnWorkOrder &&
                !userAllowedAction?.isFeedbackGivenByEngineer &&
                isWithinEngineerFeedbackWindow && (
                  <BaseButton
                    onClick={() => {
                      dispatch(
                        setFeedbackModalOpen({
                          workOrderId: workOrderDetail.id,
                          isOpen: true,
                        }),
                      );
                    }}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    fullWidth={true}
                    className="bg-primary text-white  min-w-[140px] max-w-[140px] h-8"
                  >
                    Leave Feedback
                  </BaseButton>
                )}

              {isAssignedEngineerOnWorkOrder &&
                userAllowedAction?.isFeedbackGivenByEngineer &&
                isWithinEngineerFeedbackWindow && (
                  <BaseButton
                    onClick={() => {
                      setIsEditFeedbackMode(true);
                      dispatch(
                        setFeedbackModalOpen({
                          workOrderId: workOrderDetail.id,
                          isOpen: true,
                        }),
                      );
                    }}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      // paddingLeft: 0,
                      // paddingRight: 0,
                    }}
                    className="bg-primary text-white min-w-[140px] max-w-[150px]  h-8"
                    fullWidth={true}
                  >
                    Modify Feedback
                  </BaseButton>
                )}

              {userAllowedAction?.isStartTime && (
                <div className="flex flex-wrap items-center gap-2">
                  {(workOrderDetail?.status === "INCOMPLETE" ||
                    workOrderDetail?.status === "INPROGRESS") &&
                    userAllowedAction?.isCheckOut && (
                      <BaseButton
                        style={{
                          paddingTop: 0,
                          paddingBottom: 0,
                          // paddingLeft: 0,
                          // paddingRight: 0,
                        }}
                        loaderSize="24px"
                        loaderColor="primary"
                        className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                        fullWidth={true}
                        onClick={() => setMarkCompleteModalOpen(true)}
                      >
                        Mark Complete
                      </BaseButton>
                    )}

                  {workOrderDetail?.subStatus === "READY_TO_GO" &&
                    workOrderDetail?.status !== "CANCELLED" && (
                      <BaseButton
                        onClick={handleOnTheWay}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        className="bg-primary text-white  min-w-[120px] max-w-[120px] h-8"
                        fullWidth={true}
                        disabled={!isWithinAllowedWindow}
                      >
                        On the Way
                      </BaseButton>
                    )}
                  {(workOrderDetail?.subStatus === "READY_TO_GO" ||
                    workOrderDetail?.subStatus === "ON_THE_WAY") &&
                    workOrderDetail?.status !== "CANCELLED" && (
                      <BaseButton
                        onClick={() => setRunningLateModalOpen(true)}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={true}
                        className="bg-primary text-white  min-w-[130px] max-w-[130px] h-8"
                        disabled={!isWithinAllowedWindow}
                      >
                        Running Late
                      </BaseButton>
                    )}
                  {workOrderDetail?.status === "ASSIGNED" &&
                    workOrderDetail?.subStatus !== "READY_TO_GO" && (
                      <BaseButton
                        onClick={handleCheckIn}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={true}
                        className="bg-primary text-white  min-w-[120px] max-w-[120px] h-8"
                      >
                        Check In
                      </BaseButton>
                    )}
                  {userAllowedAction?.isCheckOut &&
                    (workOrderDetail?.status === "INCOMPLETE" ||
                      workOrderDetail?.status === "INPROGRESS") && (
                      <BaseButton
                        onClick={handleCheckIn}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={true}
                        className="bg-primary text-white  min-w-[120px] max-w-[120px] h-8"
                      >
                        Check In
                      </BaseButton>
                    )}
                  {userAllowedAction?.isCheckIn &&
                    (workOrderDetail?.status === "INCOMPLETE" ||
                      workOrderDetail?.status === "INPROGRESS") && (
                      <BaseButton
                        onClick={handleCheckOut}
                        style={{
                          border: `1px solid ${brandPrimaryColor}`,
                          paddingTop: 0,
                          paddingBottom: 0,
                        }}
                        fullWidth={true}
                        className="bg-primary text-white  min-w-[120px] max-w-[120px] h-8"
                      >
                        Check out
                      </BaseButton>
                    )}
                </div>
              )}
            </div>
          )}

          {/* Customers share this row for one action only — moving their own work
              order back to draft. Everything else here stays client-only, so each
              of those keeps its own role check. */}
          {["CLIENT", "CUSTOMER"].includes(authRole) && (
            <div className="gap-2 flex flex-wrap">
              {authRole === "CLIENT" &&
                workOrderDetail?.status === WorkOrderStatusType.CANCELLED &&
                workOrderDetail?.customerStatus == null && (
                  <BaseButton
                    onClick={() => {
                      setIsMarkPayableConfirmationModalOpen(true);
                    }}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    fullWidth={true}
                    className="bg-primary text-white  min-w-[140px] max-w-[140px] h-8"
                  >
                    Mark Payable
                  </BaseButton>
                )}
              {authRole === "CLIENT" &&
                workOrderDetail?.status === WorkOrderStatusType.APPROVED &&
                hasApproveWorkOrderAccess && (
                  <BaseButton
                    onClick={() => {
                      setIsMarkDisputeConfirmationModalOpen(true);
                    }}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    fullWidth={true}
                    className="bg-primary text-white  min-w-[140px] max-w-[140px] h-8"
                  >
                    Mark Dispute
                  </BaseButton>
                )}
              {/* Both states can go back to DRAFT: a customer's ticket still
                  awaiting review, and a live one not yet taken by an engineer.
                  The label differs because only one of them is on the
                  marketplace — "Unpublish" makes no sense for the other. */}
              {["PUBLISHED", "PENDING_APPROVAL"].includes(workOrderDetail?.status) && (
                <BaseButton
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  onClick={() => setUnpublishModalOpen(true)}
                  fullWidth={true}
                  className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                >
                  {/* "Unpublish" is marketplace language a customer never sees —
                      their board ends at Accepted, so the action reads the same
                      there as it does from Submitted. */}
                  {workOrderDetail?.status === "PUBLISHED" && authRole !== UserType.CUSTOMER
                    ? "Unpublish"
                    : "Move to Draft"}
                </BaseButton>
              )}
              {authRole === "CLIENT" &&
                !userAllowedAction?.isFeedbackGivenByClient &&
                isWithinClientFeedbackWindow && (
                  <BaseButton
                    onClick={() => {
                      dispatch(
                        setFeedbackModalOpen({
                          workOrderId: workOrderDetail.id,
                          isOpen: true,
                        }),
                      );
                    }}
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    fullWidth={true}
                    className="bg-primary text-white  min-w-[140px] max-w-[140px] h-8"
                  >
                    Leave Feedback
                  </BaseButton>
                )}
              {(workOrderDetail?.status === "DRAFT" ||
                workOrderDetail.subStatus === "CANCELLED") && (
                <BaseButton
                  isLoading={isPublishDraftedWorkOrderApiLoading}
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={false}
                  className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                  onClick={handlePublishButton}
                >
                  {/* A customer cannot put work on the marketplace — publishing
                      their draft sends it to the client for review, so the action
                      is "Submit" for them (see draftToPublishWorkOrder). This
                      button also republishes a cancelled work order, which really
                      does publish, so the wording is scoped to the draft case. */}
                  {authRole === UserType.CUSTOMER && workOrderDetail?.status === "DRAFT"
                    ? "Submit"
                    : "Publish"}
                </BaseButton>
              )}
              {/* Lets the client propose different customer rates on a
                  customer-raised ticket before publishing it. Once sent it can
                  only be withdrawn until the customer answers. */}
              {canShowCounterOfferButtons &&
                !pendingCustomerCounterOffer &&
                !isCustomerCounterOfferAccepted && (
                  <BaseButton
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    fullWidth={false}
                    className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                    onClick={() => setCustomerCounterOfferModalOpen(true)}
                  >
                    Counter Offer
                  </BaseButton>
                )}
              {canShowCounterOfferButtons && pendingCustomerCounterOffer && (
                <BaseButton
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={false}
                  className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary min-w-[170px] max-w-[170px] h-8"
                  onClick={() => setEditingCustomerCounterOffer(pendingCustomerCounterOffer)}
                >
                  Edit Counter Offer
                </BaseButton>
              )}
              {canShowCounterOfferButtons && pendingCustomerCounterOffer && (
                <BaseButton
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  fullWidth={false}
                  className="bg-[#F6F6F6] hover:bg-primary hover:text-white text-primary min-w-[190px] max-w-[190px] h-8"
                  onClick={() => setWithdrawCustomerCounterOfferModalOpen(true)}
                >
                  Withdraw Counter Offer
                </BaseButton>
              )}
              {workOrderDetail?.status === WorkOrderStatusType.PENDING_APPROVAL &&
                authRole === UserType.CLIENT && (
                  <BaseButton
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    fullWidth={false}
                    className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                    onClick={handleReviewPublishButton}
                  >
                    Review &amp; Publish
                  </BaseButton>
                )}
              {workOrderDetail?.status === "INCOMPLETE" && (
                <BaseButton
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  loaderSize="24px"
                  loaderColor="primary"
                  className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                  fullWidth={true}
                  onClick={() => setMarkCompleteModalOpen(true)}
                >
                  Mark Complete
                </BaseButton>
              )}
              {workOrderDetail?.status === "WORKDONE" && (
                <BaseButton
                  style={{
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  loaderSize="24px"
                  loaderColor="primary"
                  className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                  fullWidth={true}
                  onClick={() => setMarkIncompleterModalOpen(true)}
                >
                  Mark Incomplete
                </BaseButton>
              )}
              {((authRole === UserType.CUSTOMER &&
                (workOrderDetail?.status === "WORKDONE" ||
                  workOrderDetail?.status === "VERIFIED")) ||
                (workOrderDetail?.status === "WORKDONE" &&
                  workOrderDetail?.subStatus === null &&
                  hasReviewWorkOrderAccess)) && (
                <BaseTooltip
                  content={customerApproveDisabledReason}
                  disabled={!isCustomerApproveDisabled}
                  placement="top"
                >
                  {/* span wrapper: disabled buttons don't emit hover events, so the tooltip anchors here */}
                  <span className="inline-flex">
                    <BaseButton
                      style={{
                        border: `1px solid ${brandPrimaryColor}`,
                        paddingTop: 0,
                        paddingBottom: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                      loaderSize="24px"
                      loaderColor="primary"
                      className={`bg-primary text-white h-8 ${
                        authRole === UserType.CUSTOMER
                          ? "min-w-[140px] max-w-[140px]"
                          : "min-w-[95px] max-w-[95px]"
                      }`}
                      fullWidth={true}
                      disabled={isCustomerApproveDisabled}
                      onClick={() => {
                        if (authRole === UserType.CUSTOMER) {
                          setIsCustomerApproveConfirmOpen(true);
                        } else {
                          setIsApproveModalOpen(true);
                          setIsReviewed(false);
                          setIsVerified(false);
                        }
                      }}
                      isLoading={authRole === UserType.CUSTOMER && isApproveWorkOrderLoading}
                    >
                      {authRole === UserType.CUSTOMER ? "Approve" : "Review"}
                    </BaseButton>
                  </span>
                </BaseTooltip>
              )}
              {workOrderDetail?.status === "WORKDONE" &&
                workOrderDetail?.subStatus === "REVIEWED" &&
                hasVerifyWorkOrderAccess && (
                  // || workOrderDetail?.status === "INCOMPLETE"
                  <BaseButton
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                    fullWidth={true}
                    onClick={() => {
                      setIsApproveModalOpen(true);
                      setIsReviewed(true);
                      setIsVerified(false);
                    }}
                  >
                    Verify
                  </BaseButton>
                )}
              {workOrderDetail?.status === "VERIFIED" &&
                hasApproveWorkOrderAccess &&
                authRole !== UserType.CUSTOMER && (
                  // || workOrderDetail?.status === "INCOMPLETE"
                  <BaseButton
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                    }}
                    className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                    fullWidth={true}
                    onClick={() => {
                      setIsApproveModalOpen(true);
                      setIsVerified(true);
                      setIsReviewed(true);
                    }}
                  >
                    Approve
                  </BaseButton>
                )}{" "}
              {userAllowedAction?.isFeedbackGivenByClient && isWithinClientFeedbackWindow && (
                <BaseButton
                  style={{
                    border: `1px solid ${brandPrimaryColor}`,
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                  className="bg-primary text-white min-w-[140px] max-w-[140px]  h-8"
                  fullWidth={true}
                  onClick={() => {
                    setIsEditFeedbackMode(true);
                    dispatch(
                      setFeedbackModalOpen({
                        workOrderId: workOrderDetail.id,
                        isOpen: true,
                      }),
                    );
                  }}
                >
                  Modify Feedback
                </BaseButton>
              )}
              {/* {(workOrderDetail?.status === "ASSIGNED" ||
                workOrderDetail?.status === "CONFIRM") &&
                workOrderDetail?.status !== "INPROGRESS" && (
                  <BaseButton
                    style={{
                      border: `1px solid ${brandPrimaryColor}`,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    fullWidth={false}
                    className="bg-primary text-white  min-w-[150px] max-w-[150px] h-8"
                  >
                    Add Hold
                  </BaseButton>
                )} */}
            </div>
          )}
        </div>
      </BaseWrapper>

      {isApproveModalOpen && (
        <ApproveWorkOrderModal
          isApproveModalOpen={isApproveModalOpen}
          setIsApproveModalOpen={setIsApproveModalOpen}
          workOrderDetail={workOrderDetail}
          isVerified={isVerified}
          setIsVerified={setIsVerified}
          setIsReviewed={setIsReviewed}
          isReviewed={isReviewed}
        />
      )}

      {isCustomerApproveConfirmOpen && (
        <BaseConfirmModal
          open={isCustomerApproveConfirmOpen}
          onClose={() => setIsCustomerApproveConfirmOpen(false)}
          onConfirm={async () => {
            try {
              await approveWorkOrderMutation(workOrderDetail.id);
            } catch (error) {
              // Error is handled by mutation's onError handler
            }
          }}
          title="Confirm Work Order Approval"
          description={`Are you sure you want to approve work order ${workOrderDetail?.referenceId} (${workOrderDetail?.title})?`}
          confirmText="Approve"
          cancelText="Cancel"
          isLoading={isApproveWorkOrderLoading}
          type="confirm"
        />
      )}

      {requestOfferModalOpen && (
        <RequestOfferModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          requestOfferModalOpen={requestOfferModalOpen}
          setRequestOfferModalOpen={setRequestOfferModalOpen}
          userAllowedAction={userAllowedAction}
        />
      )}
      {woConfirmationModalOpen && (
        <WOConfirmationModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          woConfirmationModalOpen={woConfirmationModalOpen}
          setWOConfirmationModalOpen={setWOConfirmationModalOpen}
          woConfirmationModalData={woConfirmationModalData}
          tasks={workOrderDetail?.tasks}
        />
      )}
      {counterOfferModalOpen && workOrderDetail.engineerPaymentType === "FULL_DAY" && (
        <FullDayCounterOfferModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          counterOfferModalOpen={counterOfferModalOpen}
          setCounterOfferModalOpen={setCounterOfferModalOpen}
          mode={counterOfferMode}
        />
      )}
      {counterOfferModalOpen && workOrderDetail.engineerPaymentType === "HALF_DAY" && (
        <HalfDayCounterOfferModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          counterOfferModalOpen={counterOfferModalOpen}
          setCounterOfferModalOpen={setCounterOfferModalOpen}
          mode={counterOfferMode}
        />
      )}

      {counterOfferModalOpen &&
        (workOrderDetail.engineerPaymentType === "HOURLY" ||
          workOrderDetail.engineerPaymentType === "FTH") && (
          <CounterOfferModal
            refetchUserAllowedAction={refetchUserAllowedAction}
            counterOfferModalOpen={counterOfferModalOpen}
            setCounterOfferModalOpen={setCounterOfferModalOpen}
            mode={counterOfferMode}
          />
        )}

      {counterOfferModalOpen && workOrderDetail?.engineerPaymentType === "FIXED" && (
        <FixedCounterOfferModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          counterOfferModalOpen={counterOfferModalOpen}
          setCounterOfferModalOpen={setCounterOfferModalOpen}
          mode={counterOfferMode}
        />
      )}

      {customerCounterOfferModalOpen && (
        <CustomerCounterOfferModal
          open={customerCounterOfferModalOpen}
          close={() => setCustomerCounterOfferModalOpen(false)}
        />
      )}

      {withdrawCustomerCounterOfferModalOpen && pendingCustomerCounterOffer && (
        <WithdrawCustomerCounterOfferModal
          open={withdrawCustomerCounterOfferModalOpen}
          close={() => setWithdrawCustomerCounterOfferModalOpen(false)}
          counterOffer={pendingCustomerCounterOffer}
        />
      )}

      {editingCustomerCounterOffer && (
        <CustomerCounterOfferModal
          open={!!editingCustomerCounterOffer}
          close={() => setEditingCustomerCounterOffer(null)}
          existingOffer={editingCustomerCounterOffer}
        />
      )}

      {markIncompleteModalOpen && (
        <MarkIncompleteModal
          markIncompleteModalOpen={markIncompleteModalOpen}
          setMarkIncompleterModalOpen={setMarkIncompleterModalOpen}
        />
      )}

      {isMarkDisputeConfirmationModalOpen && (
        <BaseConfirmModal
          open={isMarkDisputeConfirmationModalOpen}
          onClose={() => setIsMarkDisputeConfirmationModalOpen(false)}
          onConfirm={() => {
            const payload = {
              id: workOrderDetail.id,
              status: WorkOrderStatusType.DISPUTED,
            };
            handleMarkDispute(payload);
          }}
          confirmText="Confirm Dispute"
          title="Confirm Dispute"
          description={
            <div>
              <p>Are you sure you want to mark this work order as Disputed?</p>
              <p>This action cannot be undone.</p>
            </div>
          }
        />
      )}

      {isMarkPayableConfirmationModalOpen && hasVerifyWorkOrderAccess && (
        <BaseConfirmModal
          isLoading={isMarkWorkOrderAsPayableApiLoading}
          open={isMarkPayableConfirmationModalOpen}
          onClose={() => setIsMarkPayableConfirmationModalOpen(false)}
          onConfirm={() => {
            handleMarkPayable(workOrderDetail.id);
          }}
          confirmText="Mark Payable"
          title="Confirm Payable"
          description={
            <div>
              <p>Are you sure you want to mark this work order as Payable?</p>
              <p>This action cannot be undone.</p>
            </div>
          }
        />
      )}

      {markCompleteModalOpen && (
        <MarkCompleteModal
          tasks={workOrderDetail?.tasks}
          markCompleteModalOpen={markCompleteModalOpen}
          setMarkCompleteModalOpen={setMarkCompleteModalOpen}
        />
      )}

      {checkInModalOpen && (
        <CheckInModal
          refetchUserAllowedAction={refetchUserAllowedAction}
          checkInModalOpen={checkInModalOpen}
          setCheckInModalOpen={setCheckInModalOpen}
          tasks={workOrderDetail?.tasks}
        />
      )}

      {checkOutModalOpen && (
        <CheckOutModal
          checkOutModalOpen={checkOutModalOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          setCheckOutModalOpen={setCheckOutModalOpen}
          tasks={workOrderDetail?.tasks}
        />
      )}

      {onTheWayModalOpen && (
        <OnTheWayModal
          onTheWayModalOpen={onTheWayModalOpen}
          setOnTheWayModalOpen={setOnTheWayModalOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          tasks={workOrderDetail?.tasks}
        />
      )}
      {unpublishModalOpen && (
        <UnpublishModal
          unpublishModalOpen={unpublishModalOpen}
          setUnpublishModalOpen={setUnpublishModalOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          workOrderId={workOrderDetail?.id}
          workOrderStatus={workOrderDetail?.status}
          isCustomer={authRole === UserType.CUSTOMER}
          // Pending offers are the only thing lost by pulling a live work order
          // back, so the modal names the number rather than warning vaguely.
          pendingOfferCount={
            workOrderDetail?.offers?.filter((offer) =>
              ["PENDING", "COUNTER", "ROUTED"].includes(offer?.status),
            )?.length || 0
          }
        />
      )}

      {runningLateModalOpen && (
        <RunningLateModal
          runningLateModalOpen={runningLateModalOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          setRunningLateModalOpen={setRunningLateModalOpen}
          tasks={workOrderDetail?.tasks}
        />
      )}
      {cancelAssignmentModalOpen && (
        <CancelAssignmentModal
          cancelAssignmentModalOpen={cancelAssignmentModalOpen}
          setCancelAssignmentModalOpen={setCancelAssignmentModalOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          tasks={workOrderDetail?.tasks}
        />
      )}

      {cancelByClientOpen && (
        <CancelByClientModal
          cancelByClientOpen={cancelByClientOpen}
          setCancelByClientOpen={setCancelByClientOpen}
          refetchUserAllowedAction={refetchUserAllowedAction}
          tasks={workOrderDetail?.tasks}
        />
      )}

      {feedbackModalOpen &&
        feedbackModalOpen === true &&
        authRole !== UserType.CUSTOMER &&
        // Clients can own a Company (isCompanyOwner is role-agnostic), so the
        // freelance company member/owner restriction must not block them.
        (authRole === UserType.CLIENT ||
          (!userData?.isCompanyMember && !userData?.isCompanyOwner) ||
          isAssignedEngineerOnWorkOrder) && (
          <FeedbackModal
            isEdit={isEditFeedbackMode}
            refetchUserAllowedAction={refetchUserAllowedAction}
            feedbackModalOpen={feedbackModalOpen}
            setFeedbackSuccessModalOpen={setFeedbackSuccessModalOpen}
            authRole={authRole}
          />
        )}
      {feedbackSuccessModalOpen && (
        <FeedbackSuccessModal
          feedbackSuccessModalOpen={feedbackSuccessModalOpen}
          setFeedbackSuccessModalOpen={setFeedbackSuccessModalOpen}
        />
      )}

      {withdrawCounterOfferModalOpen && (
        <WithdrawCounterOfferModal
          withdrawCounterOfferModalOpen={withdrawCounterOfferModalOpen}
          setWithdrawCounterOfferModalOpen={setWithdrawCounterOfferModalOpen}
          workOrderDetail={workOrderDetail}
          refetchUserAllowedAction={refetchUserAllowedAction}
        />
      )}

      {isAddNoteModalOpen && (
        <AddWorkOrderNoteModal
          open={isAddNoteModalOpen}
          close={setIsAddNoteModalOpen}
          workOrderDetail={workOrderDetail}
        />
      )}

      {isCreateTaskModalOpen && (
        <AddEditTaskModal
          isAddEditTaskModal={isCreateTaskModalOpen}
          setIsAddEditTaskModal={setIsCreateTaskModalOpen}
          authUserTeams={auth.user?.teams}
          isCompanyOwner={userData?.isCompanyOwner}
          authUser={auth?.user}
          workOrderDetail={workOrderDetail}
          onClose={() => setIsCreateTaskModalOpen(false)}
        />
      )}

      {isProfileCompleted && (
        <CompleteProfileModal
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
        />
      )}

      {assignToMemberModalOpen && (
        <AssignToMemberModal
          open={assignToMemberModalOpen}
          onClose={() => setAssignToMemberModalOpen(false)}
          refetchUserAllowedAction={refetchUserAllowedAction}
        />
      )}
    </div>
  );
};

export default ActionBar;
